import { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useI18n } from "@/i18n";
import { usePageMeta } from "@/lib/format";
import { FAQ, searchFaq } from "@/lib/faqContent";
import { Input } from "@/components/ui/input";
import { ChevronLeft, ChevronDown, ChevronUp, Search, MessageSquare, Plus, Minus } from "lucide-react";

const CTX_LABEL = {
  general: "General", home: "Home", "deep-within": "Deep Within", goals: "My Goals",
  comfortzone: "My Comfortzone", coping: "My Coping", journal: "Journal", weva: "WEVA reports",
  "weva-chat": "WEVA Chat", breathwork: "Breathwork", coaches: "Finding a coach",
  bookings: "Sessions & bookings", privacy: "Privacy & sharing", profile: "Profile & language",
  billing: "Billing & pricing", coach_dashboard: "For coaches", beta: "Open Beta",
};

function QA({ entry, ctxLabel }) {
  const [open, setOpen] = useState(false);
  return (
    <button onClick={() => setOpen((v) => !v)} className="w-full rounded-xl border border-border bg-card p-3 text-left" data-testid="faq-item">
      <div className="flex items-start gap-2">
        {open ? <Minus className="mt-0.5 h-4 w-4 shrink-0 text-primary" /> : <Plus className="mt-0.5 h-4 w-4 shrink-0 text-primary" />}
        <div className="min-w-0 flex-1">
          {ctxLabel ? <p className="text-[10px] text-muted-foreground">{ctxLabel}</p> : null}
          <p className="text-sm font-semibold text-foreground">{entry.q}</p>
          {open ? <p className="mt-1.5 text-sm leading-relaxed text-muted-foreground">{entry.a}</p> : null}
        </div>
      </div>
    </button>
  );
}

export default function Help() {
  const { t } = useI18n();
  const navigate = useNavigate();
  usePageMeta(t("help_faq"));
  const [q, setQ] = useState("");
  const [expanded, setExpanded] = useState("general");
  const results = useMemo(() => (q.trim() ? searchFaq(q) : null), [q]);

  return (
    <div className="mx-auto max-w-2xl space-y-4" data-testid="help-screen">
      <div className="flex items-center gap-3">
        <button onClick={() => navigate("/app/account")} data-testid="help-back" className="flex h-9 w-9 items-center justify-center rounded-full bg-secondary/60 hover:bg-accent"><ChevronLeft className="h-5 w-5" /></button>
        <h1 className="flex-1 font-display text-2xl text-foreground">{t("help_faq")}</h1>
        <button onClick={() => navigate("/app/account/support")} data-testid="help-support" className="inline-flex items-center gap-1.5 rounded-full bg-primary px-3 py-1.5 text-sm font-semibold text-primary-foreground">
          <MessageSquare className="h-4 w-4" /> {t("support")}
        </button>
      </div>

      <div className="flex items-center gap-2 rounded-xl border border-border bg-card px-3 py-2">
        <Search className="h-4 w-4 text-muted-foreground" />
        <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder={t("search_all_help_articles")} className="h-8 border-0 bg-transparent px-0 focus-visible:ring-0" data-testid="help-search" />
      </div>

      {results ? (
        results.length === 0 ? (
          <p className="py-10 text-center text-sm text-muted-foreground">{t("help_search_empty", { q })}</p>
        ) : (
          <div className="space-y-2">{results.map((r, i) => <QA key={i} entry={r.entry} ctxLabel={CTX_LABEL[r.context] || r.context} />)}</div>
        )
      ) : (
        <div className="space-y-2">
          {Object.entries(FAQ).map(([ctx, entries]) => {
            const open = expanded === ctx;
            return (
              <div key={ctx} className="rounded-2xl border border-border bg-card p-3">
                <button onClick={() => setExpanded(open ? null : ctx)} data-testid={`help-section-${ctx}`} className="flex w-full items-center justify-between">
                  <span className="font-display text-sm text-foreground">{CTX_LABEL[ctx] || ctx}</span>
                  <span className="flex items-center gap-2 text-xs text-muted-foreground">{entries.length}{open ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}</span>
                </button>
                {open ? <div className="mt-2 space-y-2">{entries.map((e, i) => <QA key={i} entry={e} />)}</div> : null}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
